import { reset, SubmissionError, change } from 'redux-form';
import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';
import { getCurrencyIcon } from '../../../../../../../shared/js/get-currency-icon';

import pick from 'lodash/pick';
import omit from 'lodash/omit';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import isUndefined from 'lodash/isUndefined';
import find from 'lodash/find';

import { actions as parentActions } from '../../../list';
import { createSelectOptionsFromSysEnums } from '../../../../shared/form-elements/select';
import { formatMoney } from '../../../../shared/format';

const defaultInitialState = {
	fields: {
		affiliate_id: null,
		payout_setting_id: null,
		amount: null
	},
	affiliatePayoutSettings: null,
	affiliateBalances: null,
	isShow: false,
	disabledFields: [],
	shouldAllowFloats: false
};

const createForm = ({ namespace, stateNodeName, formName }) => {
	const FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-create-payout-form-success`;
	const FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-create-payout-form-error`;

	const TOGGLE_FORM = `${namespace}/${stateNodeName}-toggle-create-payout-form`;

	const FETCH_AFFILIATE_SETTINGS_SUCCESS = `${namespace}/${stateNodeName}-fetch-affiliate-settings-success`;
	const FETCH_AFFILIATE_SETTINGS_ERROR = `${namespace}/${stateNodeName}-fetch-affiliate-settings-error`;

	const FETCH_SUGGEST_SUCCESS = `${namespace}/${stateNodeName}-fetch-suggest-success`;
	const FETCH_SUGGEST_ERROR = `${namespace}/${stateNodeName}-fetch-suggest-error`;

	const CHANGE_ALLOW_FLOATS = `${namespace}/${stateNodeName}-change-allow-floats`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		createPayoutFormSubmit: formData => (dispatch, getState) => {
			const formState = getState()[namespace].createPayoutForm;
			const data = pick(formData, Object.keys(formState.fields));
			const {shouldAllowFloats} = formState;
			const amount = data.amount && data.amount.replace(/ /g, '');

			const dataToSubmit = {
				amount: shouldAllowFloats ? parseFloat(amount) : Math.floor(amount),
				affiliate_id: data.affiliate_id && data.affiliate_id[0] && data.affiliate_id[0].value,
				payout_setting_id: data.payout_setting_id && data.payout_setting_id[0] && data.payout_setting_id[0].value
			};

			const request = http({
				url: endpoints.PAYOUTS.BASE,
				method: 'post',
				actions: [FORM_SUBMIT_SUCCESS, FORM_SUBMIT_ERROR],
				data: dataToSubmit
			});

			return dispatch(request).then(action => {
				if (action.type === FORM_SUBMIT_SUCCESS) {
					dispatch(parentActions.fetchData());
					dispatch(actions.toggleCreatePayoutForm());
				}
				if (action.type === FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
					const responseErrors = action.payload.response.data;
					throw new SubmissionError(prepareFormErrors(responseErrors));
				}
			});
		},
		createPayoutFormGetAffiliateSettings: event => dispatch => {
			const affiliate_id = event && event[0] && event[0].value;

			dispatch(change(formName, 'payout_setting_id', null));
			dispatch(change(formName, 'amount', null));

			if (!isUndefined(affiliate_id)) {
				http({
					url: endpoints.AFFILIATES.PAYOUT_SETTINGS.replace('{id}', affiliate_id),
					method: 'get',
					actions: [FETCH_AFFILIATE_SETTINGS_SUCCESS, FETCH_AFFILIATE_SETTINGS_ERROR]
				})(dispatch);
			}
		},
		affiliatesLookups: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		},
		updateAmount: payoutSetting => (dispatch, getState) => {
			const { createPayoutForm, payoutSystems } = getState()[namespace];

			const payoutSettingId = payoutSetting && payoutSetting[0] && payoutSetting[0].value;

			if (payoutSettingId) {
				const setting = find(createPayoutForm.affiliatePayoutSettings, { id: payoutSettingId });
				const selectedSystem = find(payoutSystems, { id: setting.payout_system_id });
				const currency = selectedSystem && selectedSystem.currency;
				const allowFloats = selectedSystem && selectedSystem.allow_floats;
				const fittingBalance = find(createPayoutForm.affiliateBalances, { currency });
				const fittingBalanceAmount = fittingBalance ? fittingBalance.amount : null;
				dispatch(change(formName, 'amount', formatMoney(fittingBalanceAmount, allowFloats)));
				dispatch({ type: CHANGE_ALLOW_FLOATS, payload: allowFloats });
			}
		},
		toggleCreatePayoutForm: (disabledFields) => dispatch => {
			dispatch(reset(formName));
			dispatch({ type: TOGGLE_FORM, payload: disabledFields });
		}
	};

	// reducers
	const reducers = {
		[FETCH_AFFILIATE_SETTINGS_SUCCESS]: (state, action) => ({
			...state,
			createPayoutForm: {
				...state.createPayoutForm,
				affiliatePayoutSettings: action.payload.data[0],
				affiliateBalances: action.payload.data[1]
			}
		}),
		[TOGGLE_FORM]: (state, action) => ({
			...state,
			createPayoutForm: {
				...state.createPayoutForm,
				isShow: !state.createPayoutForm.isShow,
				disabledFields: action.payload
					? state.createPayoutForm.disabledFields.concat(action.payload)
					: state.createPayoutForm.disabledFields
			}
		}),
		[CHANGE_ALLOW_FLOATS]: (state, action) => ({
			...state,
			createPayoutForm: {
				...state.createPayoutForm,
				shouldAllowFloats: action.payload
			}
		}),
		[FORM_SUBMIT_ERROR]: errorReducerFn,
		[FETCH_AFFILIATE_SETTINGS_ERROR]: errorReducerFn
	};

	// selectors
	const selectors = {
		getFormData: state => {
			const {
				createPayoutForm: { fields, affiliatePayoutSettings, disabledFields, shouldAllowFloats },
				payoutSystems
			} = state[namespace];

			const fieldsToDisable = [].concat(disabledFields);

			const payoutSettings = map(affiliatePayoutSettings, (setting) => {
				const system = find(payoutSystems, { id: setting.payout_system_id });

				return {
					value: setting.id,
					label: `${system.title} (${getCurrencyIcon(system.currency)}) - ${getMainCredential(setting.credentials)}`
				};
			});

			if (!payoutSettings.length) {
				fieldsToDisable.push('payout_setting_id', 'amount');
			}

			return {
				selectOptions: { payoutSettings },
				initialValues: { ...fields },
				disabledFields: fieldsToDisable,
				shouldAllowFloats
			};
		},
		getShouldShowStatus: state => state[namespace].createPayoutForm.isShow
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions

function getMainCredential(credentials) {
	const keys = Object.keys(credentials);

	if (keys.length === 1) {
		return credentials[keys[0]];
	}

	if (keys.includes('name')) {
		return getMainCredential(omit(credentials, 'name'));
	}

	return credentials.card || credentials.account || credentials.phone || credentials[keys[0]];
}

const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return { ...state, error: true, showProgressBar: false };
	}
	return state;
};

const prepareFormErrors = errors => {
	const validationsErrors = {};
	forEach(errors, value => {
		const key = value.dataPath.slice(1);
		validationsErrors[key] = value.message;
	});
	return validationsErrors;
};

export default createForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/payouts/components/forms/create-payout/index.js